<template>
  <div>
    <div class="flex items-center gap-3 mb-6">
      <button @click="$emit('back')" class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        &larr; Back
      </button>
      <h2 class="text-lg font-semibold dark:text-white">
        {{ campaign.name }}
      </h2>
      <span :class="statusClass" class="px-2 py-1 rounded text-xs font-medium">{{ currentStatus }}</span>
    </div>

    <div v-if="currentStatus === 'processing'" class="mb-6">
      <div class="flex justify-between text-sm mb-1 dark:text-gray-300">
        <span>Generating posts...</span>
        <span>{{ progress.completed + progress.failed }} / {{ progress.total }}</span>
      </div>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
        <div class="bg-primary-500 rounded-full h-3 transition-all" :style="{ width: progressPercent + '%' }" />
      </div>
    </div>

    <div class="flex gap-3 mb-6">
      <a v-if="exportZipUrl && !exporting" :href="exportZipUrl" class="btn btn-primary btn-sm inline-flex items-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Download ZIP
      </a>
      <button v-else @click="exportZip" :disabled="exporting || currentStatus === 'processing'" class="btn btn-secondary btn-sm inline-flex items-center gap-2">
        <svg v-if="exporting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
        {{ exporting ? 'Preparing export...' : 'Export ZIP' }}
      </button>

      <!-- Bulk Approval -->
      <button v-if="currentStatus === 'completed' && !campaignReviewUrl"
        @click="handleSendAllForApproval" :disabled="sendingApproval"
        class="btn btn-secondary btn-sm inline-flex items-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        {{ sendingApproval ? 'Sending...' : 'Send All for Approval' }}
      </button>
      <div v-if="campaignReviewUrl" class="flex items-center gap-2">
        <input :value="campaignReviewFullUrl" readonly
          class="bg-gray-100 dark:bg-gray-900 border dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 w-64" />
        <button @click="copyReviewLink" class="text-primary-500 hover:text-primary-600 text-xs font-medium whitespace-nowrap">
          {{ reviewCopied ? 'Copied!' : 'Copy Review Link' }}
        </button>
        <span :class="approvalBadgeClass" class="text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap">
          {{ approvalStatusLabel }}
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <GbpPostCard
        v-for="post in posts"
        :key="post.id"
        :post="post"
        @view="viewingPost = post"
        @edit="editingPost = post"
        @regenerate="regeneratePost(post)" />
    </div>

    <GbpPostDetail
      v-if="viewingPost"
      :post="viewingPost"
      @edit="editingPost = viewingPost; viewingPost = null"
      @regenerate="regeneratePost(viewingPost); viewingPost = null"
      @close="viewingPost = null" />

    <GbpPostEditor
      v-if="editingPost"
      :post="editingPost"
      @saved="onPostSaved"
      @close="editingPost = null" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getGbpCampaign, exportGbpCampaign, sendAllForApproval } from '~/javascript/api/gbp_campaigns'
import { getGbpPosts, regenerateGbpPost } from '~/javascript/api/gbp_posts'
import { createConsumer } from '@rails/actioncable'
import GbpPostCard from '~/javascript/components/gbp_posts/card.vue'
import GbpPostDetail from '~/javascript/components/gbp_posts/detail.vue'
import GbpPostEditor from '~/javascript/components/gbp_posts/editor.vue'

const props = defineProps({ campaign: { type: Object, default: () => null } })
const emit = defineEmits(['back', 'updated'])

const posts = ref([])
const viewingPost = ref(null)
const editingPost = ref(null)
const exporting = ref(false)
const sendingApproval = ref(false)
const reviewCopied = ref(false)
const currentStatus = ref(props.campaign.status)
const liveProgress = ref(props.campaign.progress)
const exportZipUrl = ref(props.campaign.export_zip_url)
const campaignReviewUrl = ref(props.campaign.campaign_review_url)
const campaignApprovalStatus = ref(props.campaign.campaign_approval_status)

let exportPollTimer = null

const campaignReviewFullUrl = computed(() => {
  if (!campaignReviewUrl.value) return ''
  return `${window.location.origin}${campaignReviewUrl.value}`
})

const approvalStatusLabels = {
  pending: 'Pending',
  approved: 'All Approved',
  partially_approved: 'Partially Approved'
}
const approvalStatusLabel = computed(() => approvalStatusLabels[campaignApprovalStatus.value] || '')

const approvalBadgeClasses = {
  pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  partially_approved: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
}
const approvalBadgeClass = computed(() => approvalBadgeClasses[campaignApprovalStatus.value] || '')

const progress = computed(() => liveProgress.value || { total: 0, completed: 0, failed: 0 })
const progressPercent = computed(() => {
  if (!progress.value.total) return 0
  return Math.round(((progress.value.completed + progress.value.failed) / progress.value.total) * 100)
})

const statusClasses = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  processing: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
}
const statusClass = computed(() => statusClasses[currentStatus.value] || '')

let subscription = null

async function fetchPosts() {
  const { data } = await getGbpPosts(props.campaign.id)
  posts.value = data
}

function stopExportPolling() {
  if (exportPollTimer) {
    clearInterval(exportPollTimer)
    exportPollTimer = null
  }
}

function onExportReady(url) {
  exportZipUrl.value = url
  exporting.value = false
  stopExportPolling()
}

function setupCable() {
  const consumer = createConsumer()
  subscription = consumer.subscriptions.create(
    { channel: 'GbpCampaignChannel', campaign_id: props.campaign.id },
    {
      received(data) {
        if (data.progress) liveProgress.value = data.progress
        if (data.event === 'post_completed' || data.event === 'post_regenerated') fetchPosts()
        if (data.event === 'campaign_completed') {
          currentStatus.value = 'completed'
          emit('updated')
          fetchPosts()
        }
        if (data.event === 'export_ready') {
          onExportReady(data.download_url)
        }
      }
    }
  )
}

function startExportPolling() {
  stopExportPolling()
  let attempts = 0
  const maxAttempts = 30 // 60 seconds max

  exportPollTimer = setInterval(async() => {
    attempts += 1
    if (attempts > maxAttempts) {
      exporting.value = false
      stopExportPolling()
      return
    }
    try {
      const { data } = await getGbpCampaign(props.campaign.id)
      if (data.export_zip_url) {
        onExportReady(data.export_zip_url)
      }
    } catch (_) {
      // ignore polling errors
    }
  }, 2000)
}

async function exportZip() {
  exporting.value = true
  exportZipUrl.value = null
  await exportGbpCampaign(props.campaign.id)
  startExportPolling()
}

async function regeneratePost(post) {
  await regenerateGbpPost(post.id)
  post.status = 'generating'
}

function onPostSaved() {
  editingPost.value = null
  viewingPost.value = null
  fetchPosts()
}

async function handleSendAllForApproval() {
  sendingApproval.value = true
  try {
    const { data } = await sendAllForApproval(props.campaign.id)
    campaignReviewUrl.value = data.review_url
    campaignApprovalStatus.value = 'pending'
    fetchPosts()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Send all for approval failed:', e)
  } finally {
    sendingApproval.value = false
  }
}

function copyReviewLink() {
  navigator.clipboard.writeText(campaignReviewFullUrl.value)
  reviewCopied.value = true
  setTimeout(() => { reviewCopied.value = false }, 2000)
}

onMounted(() => {
  fetchPosts()
  setupCable()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopExportPolling()
})
</script>
